import { TenantObj } from './../state/filters';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import { selectors as authSelectors, setApiKey } from '../state/auth';
import { setDarkMode, setHeight, setLanguage } from '../state/settings';
import { setTenants, Tenant } from '../state/filters';
import { deleteJobList } from '../state/jobs';

type DashboardSettings = {
  apiKey: string;
  isDarkMode: boolean;
  language: 'de' | 'en';
  tenants: 'ALL' | string[];
  subTenants: 'ALL' | string[];
  type: 'dashboard';
  height: null | string;
};

type WidgetInfo = {
  type: WidgetType;
  action: WidgetAction;
  dashboardConfig: DashboardSettings;
};

export type WidgetType = 'bits-dashboard';
export type WidgetAction =
  | 'init'
  | 'loadConfig'
  | 'refresh'
  | 'loadConfigFinished'
  | 'refreshFinished';

const useConfig = () => {
  const dispatch = useDispatch();
  const apiKey = useSelector(authSelectors.getApiKey);

  const tenantRef = useRef<TenantObj>();
  useEffect(() => {
    window.postMessage({ type: 'bits-dashboard', action: 'init' });
  }, []);
  useEffect(() => {
    const setConfig = (config: {
      newTenants: TenantObj;
      newApiKey: string | null;
      language: 'en' | 'de';
      isDarkMode: boolean;
      height: null | string;
    }) => {
      const { newTenants, newApiKey, language, isDarkMode } = config;
      const isNewApiKey = apiKey !== newApiKey;
      const isNewTenant =
        JSON.stringify(tenantRef.current) !== JSON.stringify(newTenants);
      if (isNewApiKey && newApiKey != null) dispatch(setApiKey(newApiKey));
      dispatch(setDarkMode(isDarkMode));
      dispatch(setLanguage(language));
      if (height) dispatch(setHeight(height));

      if (isNewTenant) {
        dispatch(deleteJobList());
        dispatch(setTenants(newTenants));
        tenantRef.current = newTenants;
      }
      window.postMessage({
        type: 'bits-dashboard',
        action: 'loadConfigFinished',
      });
    };

    const queryParams = new URLSearchParams(window.location.search);
    const newTenants: TenantObj = {
      tenants: (queryParams.get('tenants') || 'ALL') as Tenant,
      subTenants: (queryParams.get('subtenants') || 'ALL') as Tenant,
    };
    const newApiKey = queryParams.get('apikey');
    const language = queryParams.get('lang') === 'en' ? 'en' : 'de';
    const isDarkMode = queryParams.get('darkmode') === '1';
    const height = queryParams.get('height') || '100vh';

    if (newApiKey) {
      setConfig({
        newApiKey,
        isDarkMode,
        language,
        newTenants,
        height,
      });
    }

    window.addEventListener('message', (event) => {
      const settings = event.data as WidgetInfo;
      if (settings.type !== 'bits-dashboard') return;
      if (settings.action === 'loadConfig') {
        const newApiKey = settings.dashboardConfig.apiKey;
        const newTenants: TenantObj = {
          tenants: settings.dashboardConfig.tenants,
          subTenants: settings.dashboardConfig.subTenants,
        };

        setConfig({
          newApiKey,
          newTenants,
          isDarkMode: settings.dashboardConfig.isDarkMode,
          language: settings.dashboardConfig.language,
          height: settings.dashboardConfig.height || '100%',
        });
      }

      if (settings.action === 'refresh') {
        const newTenants: TenantObj = {
          tenants: settings.dashboardConfig.tenants,
          subTenants: settings.dashboardConfig.subTenants,
        };
        dispatch(deleteJobList());
        dispatch(setTenants(newTenants));
        tenantRef.current = newTenants;

        window.postMessage({
          type: 'bits-dashboard',
          action: 'refreshFinished',
        });
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useConfig;
